import axios from 'axios'

const API_BASE_URL = '/api'

const subscriptionApi = axios.create({
  baseURL: `${API_BASE_URL}/subscriptions`,
  headers: {
    'Content-Type': 'application/json',
  },
})

subscriptionApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

subscriptionApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export interface SubscriptionPlan {
  plan_id: string
  name: string
  description: string
  monthly_price: number
  yearly_price: number
  benefits: string[]
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

export interface Subscriber {
  subscription_id: string
  user_id: string
  user_email: string
  user_name: string
  plan_id: string
  plan_name: string
  status: 'active' | 'cancelled' | 'expired' | 'past_due'
  billing_cycle: 'monthly' | 'yearly'
  current_period_start: string
  current_period_end: string
  cancelled_at: string | null
  cancel_reason: string | null
  created_at: string
}

export interface RevenueData {
  mrr: number
  arr: number
  total_subscribers: number
  active_subscribers: number
  plan_distribution: { plan_name: string; count: number; revenue: number }[]
  subscriber_growth: { date: string; count: number; new_subscribers: number }[]
  churn_rate: number
  average_revenue_per_user: number
}

export const subscriptionAdminService = {
  // Plans CRUD
  async getPlans(): Promise<{ success: boolean; data: SubscriptionPlan[] }> {
    const response = await subscriptionApi.get('/plans')
    return response.data
  },

  async getPlan(planId: string): Promise<{ success: boolean; data: SubscriptionPlan }> {
    const response = await subscriptionApi.get(`/plans/${planId}`)
    return response.data
  },

  async createPlan(plan: Omit<SubscriptionPlan, 'plan_id' | 'created_at' | 'updated_at'>): Promise<{ success: boolean; data: SubscriptionPlan }> {
    const response = await subscriptionApi.post('/plans', plan)
    return response.data
  },

  async updatePlan(planId: string, plan: Partial<SubscriptionPlan>): Promise<{ success: boolean; data: SubscriptionPlan }> {
    const response = await subscriptionApi.put(`/plans/${planId}`, plan)
    return response.data
  },

  async deletePlan(planId: string): Promise<{ success: boolean; message: string }> {
    const response = await subscriptionApi.delete(`/plans/${planId}`)
    return response.data
  },

  // Subscribers
  async getSubscribers(filters?: { status?: string; plan_id?: string; search?: string }): Promise<{ success: boolean; data: Subscriber[]; total: number }> {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.plan_id) params.append('plan_id', filters.plan_id)
    if (filters?.search) params.append('search', filters.search)
    const response = await subscriptionApi.get('/subscribers', { params })
    return response.data
  },

  // Revenue
  async getRevenueData(): Promise<{ success: boolean; data: RevenueData }> {
    const response = await subscriptionApi.get('/revenue')
    return response.data
  },
}

export default subscriptionAdminService
